import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './PremiumFeatures.css';

// Order matters here. Lead with the differentiators (Evergreen
// + Monetization), not table-stakes features (Live Streaming).
// Course creators looking for a webinar-funnel platform self-
// identify in the first card and the second seals the deal.
// Same reordering pattern shipped on /features.
const features = [
  {
    id: 'evergreen',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
        <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
        <line x1="12" y1="19" x2="12" y2="23" />
        <line x1="8" y1="23" x2="16" y2="23" />
      </svg>
    ),
    title: 'Your Best Workshop. Running Every Week.',
    description: 'Record it once. Canira plays it on the schedule you set, with polls, chat and your offer at the moments you choose, and sends every registrant reminders.',
    features: [
      'Automated workshops at times that suit each visitor',
      'Scheduled sessions with countdown timers',
      'Polls, chat and timed offers',
      'Reminders by email, and by text when asked for'
    ],
    link: '/features/evergreen',
    linkText: 'Explore automated webinars',
    gradient: 'linear-gradient(135deg, #EC4899 0%, #D946EF 100%)',
    iconBg: 'rgba(236, 72, 153, 0.15)'
  },
  {
    id: 'monetization',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="12" y1="1" x2="12" y2="23" />
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </svg>
    ),
    title: 'Sell directly from your platform.',
    description: 'Secure checkout and subscription billing — all built in. Payouts land in your own account, not ours.',
    features: [
      'Secure card & PayPal checkout',
      'Sell access via the landing-page checkout block',
      'Recurring subscriptions + memberships',
      'Course sales with student progress tracking'
    ],
    link: '/features',
    linkText: 'Explore monetization',
    gradient: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
    iconBg: 'rgba(16, 185, 129, 0.15)'
  },
  {
    id: 'broadcast-layer',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M5.636 18.364a9 9 0 0 1 0-12.728" />
        <path d="M18.364 5.636a9 9 0 0 1 0 12.728" />
        <path d="M8.464 15.536a5 5 0 0 1 0-7.072" />
        <path d="M15.536 8.464a5 5 0 0 1 0 7.072" />
        <circle cx="12" cy="12" r="1" fill="currentColor" />
      </svg>
    ),
    title: 'Go Live Everywhere',
    description: 'Stream to YouTube, Facebook, and Instagram at the same time — from a professional browser-based studio. No downloads. No extra software.',
    features: [
      'Simulcast to multiple platforms at once',
      '1080p HD studio — runs in your browser',
      'Scenes, overlays, banners & guest speakers',
      'Live chat, polls & Q&A with your audience'
    ],
    link: '/features',
    linkText: 'Explore broadcasting',
    gradient: 'linear-gradient(135deg, #C9A962 0%, #D4B978 100%)',
    iconBg: 'rgba(201, 169, 98, 0.15)'
  },
  {
    id: 'audience-growth',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    title: 'Podcast Studio & Distribution',
    description: 'Record, host, and distribute your podcast to Apple Podcasts, Spotify, and everywhere listeners are — with one RSS feed. No separate podcast host needed.',
    features: [
      'Built-in podcast hosting with RSS feed',
      'Distribute to Apple, Spotify & all major platforms',
      'Record episodes directly in the studio',
      'AI transcripts & show notes included'
    ],
    link: '/features',
    linkText: 'Explore podcasting',
    gradient: 'linear-gradient(135deg, #8B5CF6 0%, #D4B560 100%)',
    iconBg: 'rgba(139, 92, 246, 0.15)'
  }
];

const FeatureCard = ({ feature, index }) => {
  const cardRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setIsVisible(true), index * 150);
        }
      },
      { threshold: 0.2 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, [index]);

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePosition({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  return (
    <div
      ref={cardRef}
      className={`premium-feature-card ${isVisible ? 'visible' : ''}`}
      onMouseMove={handleMouseMove}
      style={{
        '--mouse-x': `${mousePosition.x}px`,
        '--mouse-y': `${mousePosition.y}px`,
        '--card-gradient': feature.gradient,
      }}
    >
      {/* Spotlight effect */}
      <div className="card-spotlight" />

      {/* Card content */}
      <div className="hp-card-content">
        {/* Icon */}
        <div className="hp-card-icon" style={{ background: feature.iconBg }}>
          {feature.icon}
        </div>

        {/* Title */}
        <h3 className="hp-card-title">{feature.title}</h3>

        {/* Description */}
        <p className="hp-card-description">{feature.description}</p>

        {/* Feature list */}
        <ul className="card-features">
          {feature.features.map((item, i) => (
            <li key={i} className="feature-item">
              <span className="hp-feature-check">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </span>
              <span>{item}</span>
            </li>
          ))}
        </ul>

        {/* Link */}
        <Link to={feature.link} className="card-link">
          <span>{feature.linkText}</span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="5" y1="12" x2="19" y2="12" />
            <polyline points="12 5 19 12 12 19" />
          </svg>
        </Link>
      </div>

      {/* Glow border on hover */}
      <div className="card-glow" style={{ background: feature.gradient }} />
    </div>
  );
};

const PremiumFeatures = () => {
  const headerRef = useRef(null);
  const [headerVisible, setHeaderVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHeaderVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (headerRef.current) {
      observer.observe(headerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section className="premium-features-section">
      {/* Background elements */}
      <div className="features-bg">
        <div className="bg-gradient bg-1" />
        <div className="bg-gradient bg-2" />
      </div>

      <div className="features-container">
        {/* Section Header */}
        <div ref={headerRef} className={`features-header ${headerVisible ? 'visible' : ''}`}>
          <span className="hp-section-badge">
            <span className="badge-dot" />
            Everything one workshop needs
          </span>
          <h2 className="hp-section-title">
            One place.
            <span className="gradient-text"> Before, during and after.</span>
          </h2>
          <p className="hp-section-subtitle">
            Live and automated workshops, clips, follow-up, podcasts and courses in one place, so your
            clips link back to your next registration and your follow-up knows who showed up.
          </p>
        </div>

        {/* Feature Cards Grid */}
        <div className="hp-features-grid">
          {features.map((feature, index) => (
            <FeatureCard key={feature.id} feature={feature} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default PremiumFeatures;
